import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setStoredToken, getStoredToken } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [googleEnabled, setGoogleEnabled] = useState(false);

  const refresh = useCallback(async () => {
    // Fetch auth config (public)
    try {
      const cfg = await api.get("/auth/config");
      setGoogleEnabled(!!cfg.data.googleEnabled);
    } catch {
      // ignore
    }

    if (!getStoredToken()) {
      setUser(null);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setStoredToken(null);
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = useCallback(
    async (email, password) => {
      const { data } = await api.post("/auth/login", { email, password });
      setStoredToken(data.token);
      setUser(data.user);
      return data.user;
    },
    []
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    setStoredToken(null);
    setUser(null);
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    await api.post("/auth/password-reset/request", { email });
  }, []);

  const confirmPasswordReset = useCallback(async (token, newPassword) => {
    await api.post("/auth/password-reset/confirm", { token, newPassword });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        googleEnabled,
        login,
        logout,
        refresh,
        requestPasswordReset,
        confirmPasswordReset,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export default AuthContext;
